/**
 * <mango-settings> — account settings (tabs).
 * =====================================================================
 * Tabbed shell. The Company-profile and Certifications tabs are populated from
 * GET /provider/profile/ (via the shared `loadProfile` cache). The remaining
 * data-driven tabs (Services / Locations / Staff / Performance) stay "coming
 * soon" until the profile endpoint exposes those fields; Payment is gated on
 * Stripe; FAQ and SLA are static. CSP-safe: tab buttons wired here, no inline JS.
 *
 * These panels use `data-profile-*` / `data-cert-*` hooks (NOT data-bind) because
 * the bound model here is the PROVIDER profile, not the work-order contract the
 * contract-usage lint checks. fmv_2 is never referenced.
 */
import { loadProfile } from "../profile";
import type { ProviderProfile } from "../api/client";
import { loadView, setText, toggle, date } from "./shared";

const VIEW_URL = new URL("../../views/settings.html", import.meta.url).href;

export class MangoSettings extends HTMLElement {
  static observedAttributes = ["data-tab"];

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);
    this.wireTabs();
    this.showTab(this.getAttribute("data-tab") ?? "profile");
    void this.populate();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this.showTab(this.getAttribute("data-tab") ?? "profile");
  }

  private wireTabs(): void {
    this.querySelectorAll<HTMLButtonElement>(".settings-nav-item").forEach((btn) => {
      btn.addEventListener("click", () => {
        const tab = btn.dataset.tab;
        if (tab) {
          window.location.hash = `#/settings/${tab}`;
          this.showTab(tab);
        }
      });
    });

    const sel = this.querySelector<HTMLSelectElement>(".settings-nav-select");
    if (sel) {
      sel.addEventListener("change", () => {
        const tab = sel.value;
        if (tab) {
          window.location.hash = `#/settings/${tab}`;
          this.showTab(tab);
        }
      });
    }
  }

  private showTab(tab: string): void {
    // Sync nav buttons.
    this.querySelectorAll<HTMLButtonElement>(".settings-nav-item").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.tab === tab);
    });
    // Sync the dropdown select.
    const sel = this.querySelector<HTMLSelectElement>(".settings-nav-select");
    if (sel && sel.value !== tab) sel.value = tab;
    // Show the matching panel; hide the rest.
    let matched = false;
    this.querySelectorAll<HTMLElement>("[data-panel]").forEach((panel) => {
      const show = panel.dataset.panel === tab;
      panel.hidden = !show;
      if (show) matched = true;
    });
    if (!matched) {
      const first = this.querySelector<HTMLElement>("[data-panel]");
      if (first) first.hidden = false;
    }
  }

  /** Fetch the provider profile once and fill the Profile + Certifications tabs. */
  private async populate(): Promise<void> {
    let profile: ProviderProfile;
    try {
      profile = await loadProfile();
    } catch {
      for (const sel of ["[data-profile-status]", "[data-certs-status]", "[data-svc-status]", "[data-loc-status]"]) {
        setText(this, sel, "Could not load your profile.");
      }
      return;
    }
    this.applyRoleGate(profile);
    this.fillProfile(profile);
    this.fillCerts(profile);
    this.fillServices(profile);
    this.fillLocations(profile);
  }

  /**
   * Role gating (Q6): Certifications, Payment, and Performance are Admin-only.
   * For a Staff profile, hide those nav items + panels; if the deep-linked tab is
   * one of them, fall back to the Company-profile tab. The server already withholds
   * financials; this hides the surfaces so Staff never land on a forbidden tab.
   */
  private applyRoleGate(p: ProviderProfile): void {
    if (p.role === "admin") return;
    const adminOnly = ["certs", "payment", "performance"];
    for (const tab of adminOnly) {
      const nav = this.querySelector<HTMLElement>(`.settings-nav-item[data-tab="${tab}"]`);
      if (nav) nav.style.display = "none";
      const opt = this.querySelector<HTMLOptionElement>(`.settings-nav-select option[value="${tab}"]`);
      if (opt) opt.hidden = true;
      const panel = this.querySelector<HTMLElement>(`[data-panel="${tab}"]`);
      if (panel) panel.hidden = true;
    }
    if (adminOnly.includes(this.getAttribute("data-tab") ?? "profile")) this.showTab("profile");
  }

  private fillServices(p: ProviderProfile): void {
    toggle(this, "[data-svc-status]", false);
    toggle(this, "[data-svc-card]", true);
    const cats = dedupe([
      ...toStrArray(p.service_categories),
      ...toStrArray(p.additional_capabilities),
      ...toStrArray(p.logistics_capabilities),
      ...toStrArray(p.materials_capabilities),
    ]);
    this.renderChips("[data-svc-categories]", cats);
    this.renderChips("[data-svc-equipment]", toStrArray(p.equipment_categories));
    const bio = (p.biotech_experience ?? "").toString().trim();
    toggle(this, "[data-svc-biotech-row]", !!bio);
    if (bio) setText(this, "[data-svc-biotech]", bio);
  }

  private fillLocations(p: ProviderProfile): void {
    toggle(this, "[data-loc-status]", false);
    toggle(this, "[data-loc-card]", true);
    setText(this, "[data-loc-company]", p.company_name || "Processing facility");
    setText(this, "[data-loc-street]", p.address_street || "—");
    setText(this, "[data-loc-citystate]", [p.address_city, p.address_state].filter(Boolean).join(", ") || "—");
    setText(this, "[data-loc-zip]", p.address_zip || "—");
    setText(this, "[data-loc-servicearea]", this.serviceArea(p));
  }

  /** Render a list of capability labels as pill chips into the container, or "—". */
  private renderChips(selector: string, values: string[]): void {
    const container = this.querySelector<HTMLElement>(selector);
    const tpl = this.querySelector<HTMLTemplateElement>("template[data-svc-chip]");
    if (!container || !tpl) return;
    container.replaceChildren();
    if (!values.length) {
      container.textContent = "—";
      return;
    }
    for (const v of values) {
      const frag = tpl.content.cloneNode(true) as DocumentFragment;
      setText(frag, "[data-svc-chip-el]", humanize(v));
      container.appendChild(frag);
    }
  }

  private fillProfile(p: ProviderProfile): void {
    setText(this, "[data-profile-company]", p.company_name || "—");
    setText(this, "[data-profile-code]", p.public_code || "");
    setText(this, "[data-profile-role]", p.role === "admin" ? "Admin" : "Staff");
    setText(this, "[data-profile-statusval]", titleCase(p.status));
    setText(this, "[data-profile-contact]", p.primary_contact_name || "—");
    setText(this, "[data-profile-phone]", p.phone || "—");
    setText(this, "[data-profile-website]", p.website || "—");
    const loc = [p.address_city, p.address_state].filter(Boolean).join(", ");
    setText(this, "[data-profile-location]", loc || "—");
    setText(this, "[data-profile-servicearea]", this.serviceArea(p));
    setText(this, "[data-profile-agreement]", p.agreement_accepted ? "Accepted" : "Not on file");
    const badge = this.querySelector<HTMLElement>("[data-profile-preferred]");
    if (badge) badge.hidden = !p.is_preferred;
    toggle(this, "[data-profile-status]", false);
    toggle(this, "[data-profile-card]", true);
  }

  private serviceArea(p: ProviderProfile): string {
    const parts: string[] = [];
    if (p.service_metro_areas) parts.push(String(p.service_metro_areas));
    if (p.service_radius_miles != null) parts.push(`${p.service_radius_miles} mi radius`);
    if (p.service_area_flexible) parts.push("flexible");
    return parts.length ? parts.join(" · ") : "—";
  }

  private fillCerts(p: ProviderProfile): void {
    const list = this.querySelector<HTMLElement>("[data-certs-list]");
    const tpl = this.querySelector<HTMLTemplateElement>("template[data-cert-row]");
    toggle(this, "[data-certs-status]", false);
    if (list && tpl) {
      list.replaceChildren();
      const certs = p.certifications ?? [];
      for (const c of certs) {
        const frag = tpl.content.cloneNode(true) as DocumentFragment;
        const item = frag.querySelector<HTMLElement>(".deliverable-item");
        if (item) item.classList.add(c.verified ? "complete" : "pending");
        setText(frag, "[data-cert-name]", c.doc_type_display || c.doc_type || "Certification");
        setText(frag, "[data-cert-id]", c.cert_id ? `#${c.cert_id}` : "");
        setText(frag, "[data-cert-expiry]", c.expiry_date ? `expires ${date(c.expiry_date)}` : "");
        setText(frag, "[data-cert-verified]", c.verified ? "Verified" : "Pending review");
        list.appendChild(frag);
      }
      toggle(this, "[data-certs-empty]", certs.length === 0);
    }
    // COI status row.
    const coi = p.coi;
    const coiItem = this.querySelector<HTMLElement>("[data-coi-item]");
    const present = !!coi?.present;
    const verified = !!coi?.verified;
    if (coiItem) coiItem.classList.add(verified ? "complete" : "pending");
    const coiLabel = !present
      ? "Not on file"
      : verified
        ? `Verified${coi?.expiry_date ? ` · expires ${date(coi.expiry_date)}` : ""}`
        : "On file (pending review)";
    setText(this, "[data-coi-status]", coiLabel);
  }
}

function titleCase(s: string | null | undefined): string {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Coerce a JSON capability value (typed loosely by the generated client) to string[]. */
function toStrArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((v) => String(v)).filter(Boolean) : [];
}

function dedupe(values: string[]): string[] {
  return [...new Set(values)];
}

/** Display-name overrides for slugs whose auto-humanized form would be wrong.
 *  Keys are lowercase slug (underscores or hyphens); values are the display string. */
const SLUG_LABELS: Record<string, string> = {
  "e-waste-recycling": "E-Waste Recycling",
  "e_waste_recycling": "E-Waste Recycling",
  "e-waste": "E-Waste",
  "e_waste": "E-Waste",
};

/** "certified_data_wipe" / "e-waste-recycling" → "Certified data wipe" / "E-Waste Recycling". */
function humanize(s: string): string {
  const override = SLUG_LABELS[s.toLowerCase()];
  if (override) return override;
  const t = s.replace(/[_-]+/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : s;
}

export function registerMangoSettings(): void {
  if (!customElements.get("mango-settings")) {
    customElements.define("mango-settings", MangoSettings);
  }
}
